import { FORM_NAMES } from "constants/index"
import React, { useState, useEffect } from "react"
import { connect } from "react-redux"
import { Nav, TabContent, TabPane, NavLink, Card, CardBody } from "reactstrap"
import { FormSection, reduxForm } from "redux-form"
import {
  cloneMenuFeature,
  getQuestionnaire,
  getQuestionnaireList,
  hideLockWarningModal,
  resetQuestionnaire,
} from "store/actions"
import Questionnaire from "./questionnaire"
import validate from "./questionnaireValidation"
import CreateResult from "./createResult"
import CreateDesign from "./createDesign"
import PropTypes from "prop-types"
import ViewResultsTab from "./viewResultsTab"
import MenuFeatureHyperlinks from "../menuFeatureHyperlinks"
import { getPointsAxios } from "helpers/api_helper"
import ModalComponent from "components/Modals"
import { CloneFeatureModal } from "../utils"
import { LoadingIndicator } from "components/LoadingIndicator"

let QuestionnaireWizard = props => {
  const TABS = {
    CREATE_QUESTIONNAIRE: 1,
    CREATE_RESULT: 2,
    VIEW_RESULT: 3,
    DESIGN: 4,
    HYPERLINKS: 5,
  }
  const [hasReplies, setHasReplies] = useState(false)

  useEffect(() => {
    const getHasReplies = async () => {
      try {
        const { has_replies } = await getPointsAxios(
          `/questionnaire/${props.feature.entity_id}/has-replies`
        )
        setHasReplies(has_replies)
      } catch (error) {
        console.error(error)
      }
    }

    if (props.feature.entity_id) {
      getHasReplies()
      props.getQuestionnaire({ id: props.feature.entity_id })
    }

    return () => {
      props.resetQuestionnaire()
    }
  }, [])

  const [activeTab, setActiveTab] = useState(TABS.CREATE_QUESTIONNAIRE)
  const markAsActive = tab => {
    return activeTab === tab ? "active" : ""
  }
  return (
    <React.Fragment>
      <FormSection name={props.form}>
        {props?.loading ? (
          <LoadingIndicator />
        ) : (
          <Card>
            <CardBody className="shadow-sm p-1">
              <Nav tabs pills>
                <NavLink
                  className={markAsActive(TABS.CREATE_QUESTIONNAIRE)}
                  onClick={() => setActiveTab(TABS.CREATE_QUESTIONNAIRE)}
                >
                  Create Questionnaire
                </NavLink>
                <NavLink
                  className={markAsActive(TABS.CREATE_RESULT)}
                  onClick={() => setActiveTab(TABS.CREATE_RESULT)}
                >
                  Create Result Screens
                </NavLink>
                <NavLink
                  className={markAsActive(TABS.VIEW_RESULT)}
                  onClick={() => setActiveTab(TABS.VIEW_RESULT)}
                >
                  View Results
                </NavLink>
                <NavLink
                  className={markAsActive(TABS.DESIGN)}
                  onClick={() => setActiveTab(TABS.DESIGN)}
                >
                  Add Design
                </NavLink>
                {props?.isEventFeature &&
                  !props?.isListItem &&
                  (!!props?.vendorConfig?.nfc_hyperlinks ||
                    !!props?.vendorConfig?.communication_hyperlinks) && (
                    <NavLink
                      className={markAsActive(TABS.HYPERLINKS)}
                      onClick={() => setActiveTab(TABS.HYPERLINKS)}
                    >
                      Hyperlinks
                    </NavLink>
                  )}
              </Nav>
              <TabContent activeTab={activeTab}>
                <TabPane
                  tabId={TABS.CREATE_QUESTIONNAIRE}
                  style={{ animation: "none" }}
                >
                  <FormSection name="create">
                    <Questionnaire
                      feature={props.feature}
                      handleAddListItem={props.handleAddListItem}
                      isListItem={props.isListItem}
                      backButtonHandler={props.backButtonHandler}
                      design={props.design}
                      hasReplies={hasReplies}
                    />
                  </FormSection>
                </TabPane>
                <TabPane
                  tabId={TABS.CREATE_RESULT}
                  style={{ animation: "none" }}
                >
                  <FormSection name="createResult">
                    <CreateResult
                      feature={props.feature}
                      design={props.design}
                      hasReplies={hasReplies}
                    />
                  </FormSection>
                </TabPane>
                <TabPane tabId={TABS.VIEW_RESULT} style={{ animation: "none" }}>
                  <ViewResultsTab />
                </TabPane>
                <TabPane tabId={TABS.DESIGN} style={{ animation: "none" }}>
                  <FormSection name="createDesign">
                    <CreateDesign
                      feature={props.feature}
                      design={props.design}
                    />
                  </FormSection>
                </TabPane>
                {props?.isEventFeature &&
                  !props?.isListItem &&
                  (!!props?.vendorConfig?.nfc_hyperlinks ||
                    !!props?.vendorConfig?.communication_hyperlinks) && (
                    <TabPane
                      tabId={TABS.HYPERLINKS}
                      style={{ animation: "none" }}
                    >
                      <MenuFeatureHyperlinks
                        event_id={props?.selectedEvent?.id}
                        menu_feature={props?.feature}
                      />
                    </TabPane>
                  )}
              </TabContent>
            </CardBody>
          </Card>
        )}
      </FormSection>
      <ModalComponent
        title={`Clone Questionnaire Menu Feature`}
        open={props.lockWarningModalIsOpen}
        onClose={props.hideLockWarningModal}
        returnFocusAfterClose={false}
      >
        <CloneFeatureModal
          handleClone={() => {
            props?.cloneMenuFeature({
              data: {
                vendor_id: +props?.vendorId,
                entity_type: "questionnaire",
                entity_id: props?.questionnaire?.id,
                menu_item_id: props?.questionnaire?.menu_item_id,
              },
              callback: props?.isListItem
                ? () => {
                    props.getQuestionnaireList({
                      menu_item_id: props.questionnaire?.menu_item_id,
                      vendor_id: parseInt(props.vendorId),
                      ...(!!props?.selectedEvent
                        ? { event_id: props.selectedEvent.id }
                        : {}),
                    })
                  }
                : null,
            })
          }}
          handleCloseModal={props.hideLockWarningModal}
          featureName={"Questionnaire"}
          loading={props?.loading}
        />
      </ModalComponent>
    </React.Fragment>
  )
}

QuestionnaireWizard = reduxForm({
  form: FORM_NAMES.QUESTIONNAIRE,
  validate,
  initialValues: {
    questionnaire_form: {
      create: {
        retakeText: "Try Again",
        retakeOption: "1",
        questions: [
          {
            questionType: "1",
            answers: [
              { answer: "", value: 0 },
              { answer: "", value: 0 },
            ],
            id: null,
          },
        ],
      },
      createResult: {
        storingPeriod: "90",
        results: [
          {
            minScore: "0",
            maxScore: "1",
            topGradientColor: "#AFECA7",
            bottomGradientColor: "#94BE82",
            textColor: "#000000",
          },
        ],
      },
      createDesign: {
        backgroundColor: "#FFFFFF",
        textColor: "#000000",
        buttonBgColor: "#000000",
        buttonTextColor: "#FFFFFF",
        submitText: "Submit",
      },
    },
  },
})(QuestionnaireWizard)

const mapStateToProps = state => ({
  selectedEvent: state.experiences.selectedEvent,
  vendorConfig: state.clients.vendorConfig,
  vendorId: state.Login.vendorId,
  lockWarningModalIsOpen: state.menuFeatures.lockWarningModalIsOpen,
  loading: state.menuFeatures.loading,
  questionnaire: state.Questionnaire.questionnaire,
  loading: state.Questionnaire.loading,
})

const mapDispatchToProps = dispatch => ({
  getQuestionnaire: values => dispatch(getQuestionnaire(values)),
  resetQuestionnaire: () => dispatch(resetQuestionnaire()),
  cloneMenuFeature: values => dispatch(cloneMenuFeature(values)),
  hideLockWarningModal: () => dispatch(hideLockWarningModal()),
  getSurveyList: values => dispatch(getSurveyList(values)),
  getQuestionnaireList: values => dispatch(getQuestionnaireList(values)),
})

QuestionnaireWizard.propTypes = {
  feature: PropTypes.object,
  handleAddListItem: PropTypes.func,
  isListItem: PropTypes.bool,
  backButtonHandler: PropTypes.func,
}

export default connect(mapStateToProps, mapDispatchToProps)(QuestionnaireWizard)
